/**
 * Team Members Settings Page
 *
 * Displays all workspace members with server-side pagination,
 * sorting, and filtering.
 *
 * Features:
 * - Server-side data grid with TanStack Query
 * - Role management
 * - Member removal
 * - Invite new members
 */

import { useState, useMemo, useCallback } from 'react';
import { Users, Plus, Mail } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { Button } from '@loader/ui';
import type { WorkspaceRole } from '@loader/shared-types';

import {
  useMembersColumns,
  memberToUnifiedRow,
  type UnifiedMemberRow,
} from '../hooks/useMembersColumns';

import {
  DataTable,
  useDataGrid,
} from '@/components/data-grid';
import {
  FoldingCube,
  EmptyView,
  ConfirmDialog,
} from '@/components/common';
import { UpgradeDialog } from '@/components/billing';
import { InviteMemberDialog } from '@/components/workspace/InviteMemberDialog';
import { useWorkspaceStore } from '@/store/useWorkspaceStore';
import { useUsageEnforcement, useWorkspaceMembership } from '@/hooks';
import { useRpc } from '@/lib/rpc';
import { toast } from '@/lib/toast';
import type { WorkspaceMember, PendingInvitation } from '@/lib/types';

// ============================================================================
// Main Component
// ============================================================================

/**
 * Members Page Component
 *
 * Displays all workspace members with server-side data grid.
 */
export function MembersPage() {
  // ============================================================================
  // Hooks & State
  // ============================================================================

  const { selectedWorkspaceId } = useWorkspaceStore();
  const { user: clerkUser } = useUser();
  const rpc = useRpc();

  // Local UI state
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<WorkspaceMember | null>(null);
  const [updatingRoles, setUpdatingRoles] = useState<Set<string>>(new Set());
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [limitDialogData, setLimitDialogData] = useState<{ count: number; limit: number } | null>(null);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [invitationToRevoke, setInvitationToRevoke] = useState<PendingInvitation | null>(null);
  const [resendingInvitations, setResendingInvitations] = useState<Set<string>>(new Set());

  // Usage enforcement
  const { canAddTeamMember } = useUsageEnforcement(selectedWorkspaceId || '');

  // Get current user's membership info
  const currentUserEmail = clerkUser?.primaryEmailAddress?.emailAddress?.toLowerCase();

  // ============================================================================
  // Data Fetching
  // ============================================================================

  // Fetch current user's workspace role to check owner access
  const { isOwner, isLoading: isMembershipLoading } = useWorkspaceMembership();

  // Server-side data grid with TanStack Query
  // Only fetch members if user is owner
  const grid = useDataGrid<WorkspaceMember>({
    endpoint: 'workspace-members',
    extraParams: { workspace_id: selectedWorkspaceId },
    enabled: !!selectedWorkspaceId && isOwner,
    initialPageSize: 20,
    initialSortBy: 'created_at',
    initialSortDir: 'desc',
    queryKeyPrefix: 'workspace-members',
  });

  // Find current user in members list
  const currentUserMember = useMemo(() => {
    if (!currentUserEmail || !grid.data) return null;
    return (grid.data as WorkspaceMember[]).find(
      (m) => m.user?.email?.toLowerCase() === currentUserEmail
    );
  }, [currentUserEmail, grid.data]);

  const currentUserRole = currentUserMember?.role || null;
  const currentUserId = currentUserMember?.user_id || null;
  const canChangeRoles = currentUserRole === 'owner';

  // Transform members into unified rows
  // Pending invitations (clerk_user_id = null) are identified in memberToUnifiedRow
  const unifiedRows = useMemo<UnifiedMemberRow[]>(() => {
    const rows = (grid.data as WorkspaceMember[] || []).map((m) =>
      memberToUnifiedRow(m, currentUserId)
    );
    // Sort: pending invitations first, then members by join date
    return rows.sort((a, b) => {
      if (a.type === 'invitation' && b.type !== 'invitation') return -1;
      if (a.type !== 'invitation' && b.type === 'invitation') return 1;
      return 0;
    });
  }, [grid.data, currentUserId]);

  // Total count from grid
  const totalCount = grid.pagination.total || 0;

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Handle role change
   */
  const handleRoleChange = useCallback(
    async (memberId: string, userId: string, newRole: WorkspaceRole) => {
      if (!selectedWorkspaceId) return;

      try {
        setUpdatingRoles((prev) => new Set(prev).add(memberId));

        // @ts-expect-error - Hono RPC type inference limitation for nested routes
        const res = await rpc.api.private.workspaces[':id'].members[':userId'].$patch({
          param: { id: selectedWorkspaceId, userId },
          json: { role: newRole },
        });

        if (!res.ok) {
          throw new Error('Failed to update role');
        }

        toast.member.roleUpdated(newRole);

        // Refresh grid data
        grid.actions.refetch();
      } catch (error) {
        toast.member.roleUpdateFailed(error instanceof Error ? error.message : undefined);
      } finally {
        setUpdatingRoles((prev) => {
          const next = new Set(prev);
          next.delete(memberId);
          return next;
        });
      }
    },
    [selectedWorkspaceId, rpc, grid.actions]
  );

  /**
   * Open remove member dialog
   */
  const handleRemoveMember = useCallback((member: WorkspaceMember) => {
    setMemberToRemove(member);
    setRemoveDialogOpen(true);
  }, []);

  /**
   * Confirm and execute member removal
   */
  const handleConfirmRemove = useCallback(async () => {
    if (!memberToRemove || !selectedWorkspaceId) return;

    try {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].members[':userId'].$delete({
        param: { id: selectedWorkspaceId, userId: memberToRemove.user_id },
      });

      if (!res.ok) {
        throw new Error('Failed to remove member');
      }

      // Refresh grid data
      grid.actions.refetch();

      // Close dialog
      setRemoveDialogOpen(false);
      setMemberToRemove(null);
    } catch (error) {
      toast.member.removeFailed(error instanceof Error ? error.message : undefined);
    }
  }, [memberToRemove, selectedWorkspaceId, rpc, grid.actions]);

  /**
   * Handle invite member dialog open
   */
  const handleInviteMember = useCallback(() => {
    const result = canAddTeamMember();
    // If still loading subscription data, open dialog anyway (server will validate)
    if (result.isLoading) {
      setIsInviteDialogOpen(true);
      return;
    }
    if (!result.allowed) {
      setLimitDialogData({
        count: result.current ?? 0,
        limit: result.limit ?? 0,
      });
      setShowLimitDialog(true);
      return;
    }
    setIsInviteDialogOpen(true);
  }, [canAddTeamMember]);

  /**
   * Handle successful invitation
   */
  const handleInviteSuccess = useCallback(() => {
    // Refresh members list after successful invitation
    grid.actions.refetch();
  }, [grid.actions]);

  /**
   * Handle resend invitation
   */
  const handleResendInvitation = useCallback(
    async (invitation: PendingInvitation) => {
      if (!selectedWorkspaceId) return;

      try {
        setResendingInvitations((prev) => new Set(prev).add(invitation.id));

        // @ts-expect-error - Hono RPC type inference limitation for nested routes
        const res = await rpc.api.private.workspaces[':id'].invitations[':invitationId'].resend.$post({
          param: { id: selectedWorkspaceId, invitationId: invitation.id },
        });

        if (!res.ok) {
          throw new Error('Failed to resend invitation');
        }

        toast.success('Invitation resent', `Invitation email sent to ${invitation.email}`);
        grid.actions.refetch();
      } catch (error) {
        toast.error('Failed to resend', error instanceof Error ? error.message : 'Could not resend invitation');
      } finally {
        setResendingInvitations((prev) => {
          const next = new Set(prev);
          next.delete(invitation.id);
          return next;
        });
      }
    },
    [selectedWorkspaceId, rpc, grid.actions]
  );

  /**
   * Handle revoke invitation
   */
  const handleRevokeInvitation = useCallback((invitation: PendingInvitation) => {
    setInvitationToRevoke(invitation);
    setRevokeDialogOpen(true);
  }, []);

  /**
   * Confirm and execute invitation revoke
   */
  const handleConfirmRevoke = useCallback(async () => {
    if (!invitationToRevoke || !selectedWorkspaceId) return;

    try {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].invitations[':invitationId'].$delete({
        param: { id: selectedWorkspaceId, invitationId: invitationToRevoke.id },
      });

      if (!res.ok) {
        throw new Error('Failed to revoke invitation');
      }

      toast.success('Invitation revoked', `Invitation to ${invitationToRevoke.email} has been revoked`);
      grid.actions.refetch();
      setRevokeDialogOpen(false);
      setInvitationToRevoke(null);
    } catch (error) {
      toast.error('Failed to revoke', error instanceof Error ? error.message : 'Could not revoke invitation');
    }
  }, [invitationToRevoke, selectedWorkspaceId, rpc, grid.actions]);

  // ============================================================================
  // Column Definitions (extracted to hook)
  // ============================================================================

  const columns = useMembersColumns({
    canChangeRoles,
    currentUserRole,
    updatingRoles,
    resendingInvitations,
    onRoleChange: handleRoleChange,
    onRemoveMember: handleRemoveMember,
    onResendInvitation: handleResendInvitation,
    onRevokeInvitation: handleRevokeInvitation,
  });

  // ============================================================================
  // Early Returns (Loading, Error, No Workspace, Access Check)
  // ============================================================================

  if (!selectedWorkspaceId) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">Please select a workspace to view members.</div>
      </div>
    );
  }

  if (isMembershipLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <FoldingCube text="Loading..." />
      </div>
    );
  }

  // Show access restricted for non-owners
  if (!isMembershipLoading && !isOwner) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Users className="text-muted-foreground mb-4 h-12 w-12" />
        <h2 className="text-xl font-semibold">Access Restricted</h2>
        <p className="text-muted-foreground mt-2 max-w-md text-center">
          Only workspace owners can view and manage team members. Contact your workspace owner for assistance.
        </p>
      </div>
    );
  }

  if (grid.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <FoldingCube text="Loading members..." />
      </div>
    );
  }

  if (grid.error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-destructive">Error: {grid.error.message}</div>
      </div>
    );
  }

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="absolute inset-4 top-0 flex flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {/* Page Header: align with tables/linked tables layouts */}
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Team Members</h1>
            <p className="text-muted-foreground">
              Manage your workspace members and their permissions
              {totalCount > 0 ? ` (${totalCount} total)` : ''}
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleInviteMember}
            disabled={!selectedWorkspaceId}
            className="shrink-0"
          >
            <Plus className="mr-2 h-4 w-4" />
            Invite Member
          </Button>
        </header>

        {/* Unified Members Table (includes pending invitations) */}
        <div className="min-h-0 flex-1">
          <DataTable
            data={unifiedRows}
            columns={columns}
            // Pagination props (required by DataTable but not used with enablePagination=false)
            pageIndex={0}
            pageSize={unifiedRows.length || 20}
            totalItems={unifiedRows.length}
            pageCount={1}
            // UI options
            enableFiltering={false}
            enablePagination={false}
            showFooter={false}
            hideVerticalLines={true}
            emptyStateComponent={
              <EmptyView
                title="No members found"
                description="Collaborate with your team by inviting members to your workspace. Members can view, edit, and manage your data based on their assigned roles."
                icon={Users}
              >
                <Button onClick={handleInviteMember}>
                  <Mail className="mr-2 h-4 w-4" />
                  Invite your first member
                </Button>
              </EmptyView>
            }
          />
        </div>
      </div>

      {/* Invite Member Dialog */}
      {selectedWorkspaceId && (
        <InviteMemberDialog
          open={isInviteDialogOpen}
          onOpenChange={setIsInviteDialogOpen}
          workspaceId={selectedWorkspaceId}
          onInviteSuccess={handleInviteSuccess}
        />
      )}

      {/* Remove Member Confirmation Dialog */}
      <ConfirmDialog
        open={removeDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setRemoveDialogOpen(false);
            setMemberToRemove(null);
          }
        }}
        title="Remove Member"
        description={
          memberToRemove
            ? `Are you sure you want to remove ${memberToRemove.user?.email || 'this user'} from this workspace?`
            : ''
        }
        confirmText="Remove"
        variant="destructive"
        onConfirm={handleConfirmRemove}
      />

      {/* Revoke Invitation Confirmation Dialog */}
      <ConfirmDialog
        open={revokeDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setRevokeDialogOpen(false);
            setInvitationToRevoke(null);
          }
        }}
        title="Revoke Invitation"
        description={
          invitationToRevoke
            ? `Are you sure you want to revoke the invitation for ${invitationToRevoke.email}? They will no longer be able to join this workspace using the invitation link.`
            : ''
        }
        confirmText="Revoke"
        variant="destructive"
        onConfirm={handleConfirmRevoke}
      />

      {/* Team Member Limit Dialog */}
      {limitDialogData && (
        <UpgradeDialog
          context="team_limit"
          open={showLimitDialog}
          onOpenChange={setShowLimitDialog}
          memberCount={limitDialogData.count}
          memberLimit={limitDialogData.limit}
          workspaceId={selectedWorkspaceId || ''}
        />
      )}
    </div>
  );
}
